import { BuildFlags } from "../../../constants";

import { buildThemeFromSpec } from "./buildThemeFromSpec";
import { createBarGradientPlugin, createLineFillGradientPlugin } from "./chartjs-plugins";
import { Color } from "./color";
import { disneyCss, disneyHtml, disneySetup } from "./disney";
import { BUILTIN_PALETTES, BuiltinPalette } from "./palettes";
import { isCustomThemeId, isPaletteThemeId } from "./themeIds";
import { Theme, ThemeSpec } from "./types";
import { voxelCss, voxelSetup } from "./voxel";

/** Palette selector mode toggle. */
export type ThemeMode = "light" | "dark";
/** Palette selector background (gradient) toggle. */
export type ThemeBackground = "on" | "off";

// Re-exported from the leaf themeIds module (see its header for why it must stay dependency-free).
export { CUSTOM_THEME_ID_PREFIX, isCustomThemeId, isToggleAwareThemeId } from "./themeIds";

// Dashboards with no persisted theme_id (fresh builds persist None) land on the Ocean palette,
// which participates in the mode/background toggles — unlike legacy slug themes, which ignore
// them and render as authored.
export const DEFAULT_THEME_ID = "palette:ocean";

// Legacy null-fallbacks for the selector toggles: dashboards saved before the palette model have
// no mode/background in their config and render as light + gradient-on.
export const DEFAULT_THEME_MODE: ThemeMode = "light";
export const DEFAULT_THEME_BACKGROUND: ThemeBackground = "on";

const ChartjsPluginDefaultOptions = {
  barGradient: {
    lightTheme: {
      startLuminosity: 1.1,
      endLuminosity: 0.9,
      startOpacity: 0.7,
      endOpacity: 0.75,
      gradientEndPosition: 0.5,
    },
    darkTheme: {
      startLuminosity: 0.9,
      endLuminosity: 1.0,
      startOpacity: 0.75,
      endOpacity: 0.8,
      gradientEndPosition: 0.5,
    },
  },
  lineFillGradient: {
    lightTheme: {
      bottomOpacity: 0.05,
      topOpacity: 0.5,
      topLuminosity: 1.0,
      bottomLuminosity: 1.1,
      gradientEndPosition: 0.5,
    },
    darkTheme: {
      bottomOpacity: 0.1,
      topOpacity: 0.6,
      topLuminosity: 0.9,
      bottomLuminosity: 1.0,
      gradientEndPosition: 0.5,
    },
  },
};

export const themes: Theme[] = [
  // ================================
  // Light Theme (Default)
  // ================================
  {
    themeId: "light",
    themeName: "Light",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif",
        borderRadius: "0.6rem",
        cardBackgroundGradients: [
          "from-blue-500 to-purple-600",
          "from-indigo-600 to-violet-700",
          "from-cyan-500 to-blue-600",
          "from-violet-600 to-indigo-700",
          "from-emerald-500 to-teal-600",
          "from-amber-500 to-orange-600",
        ],
      },
      main: {
        backgroundColor: "#FAFAFA",
        secondaryBackgroundColor: "#F5F5F5",
        tertiaryBackgroundColor: "#EBEBEB",
        chipBackgroundColor: "#DEE9FE", // blue/100

        foregroundColor: "#111827",
        mutedForegroundColor: "#6B7280",
        borderColor: "#E5E7EB",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(0, 70, 100, 0.1)",
          hoverForegroundColor: "#111827",
        },
      },
      nav: {
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        foregroundColor: "#111827",
        borderColor: "#E5E7EB",
        shadow: "0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)",
        textButton: {
          hoverBackgroundColor: "rgba(0, 70, 100, 0.1)",
          hoverForegroundColor: "#111827",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        foregroundColor: "#111827",
        borderColor: "#E5E7EB",
        borderWidth: "1px",
        shadow: "0 1px 4px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)",
        textButton: {
          hoverBackgroundColor: "#F3F4F6",
          hoverForegroundColor: "#111827",
        },
      },
      textBlock: {
        background: "linear-gradient(116.26deg, #153AF0 43.88%, #528FFF 100%)",
      },
      primaryButton: {
        backgroundColor: "#153AF0", // coeff-blue-700
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#153AF0").lighten(0.8).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#7C3AED", // violet-600 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#7C3AED").lighten(0.9).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#374151",
      defaultBackgroundColor: "rgba(79, 70, 229, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#3B82F6", // blue-500
        "#10B981", // emerald-500
        "#F59E0B", // amber-500
        "#4F46E5", // indigo-600
        "#EF4444", // red-500
        "#06B6D4", // cyan-500
        "#EC4899", // pink-500
        "#8B5CF6", // violet-500
        "#F97316", // orange-500
        "#22C55E", // green-500
        "#14B8A6", // teal-500
        "#A855F7", // purple-500
      ],
      gridColor: "#E5E7EB",
      gridBorderColor: "#D1D5DB",
      fontFamily: "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1F2937",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#374151",
      legendTextColor: "#374151",
      axisTickColor: "#6B7280",
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      barBackgroundOpacity: 0.7,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.lightTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.lightTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Dark Theme
  // ================================
  {
    themeId: "dark",
    themeName: "Dark",
    logoVariant: "light_text",
    tailwind: {
      general: {
        fontFamily: "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif",
        borderRadius: "0.6rem",
        cardBackgroundGradients: [
          "from-slate-700 to-slate-900",
          "from-indigo-600 to-purple-800",
          "from-gray-700 to-gray-900",
          "from-blue-700 to-indigo-900",
          "from-purple-700 to-indigo-900",
          "from-cyan-700 to-blue-900",
        ],
        isDarkTheme: true,
      },
      main: {
        backgroundColor: "#111827", // gray-900
        foregroundColor: "oklch(92.8% 0.006 264.531)", // gray-200
        mutedForegroundColor: "#9CA3AF", // gray-400
        borderColor: "#2A313C",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(55, 65, 81, 0.95)", // gray-700 with transparency,
          hoverForegroundColor: "oklch(92.8% 0.006 264.531)",
        },
      },
      nav: {
        backgroundColor: "rgba(31, 41, 55, 0.98)",
        foregroundColor: "oklch(92.8% 0.006 264.531)",
        borderColor: "#374151",
        shadow: "0 1px 3px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.2)",
        textButton: {
          hoverBackgroundColor: "rgba(55, 65, 81, 0.95)", // gray-700 with transparency,
          hoverForegroundColor: "oklch(92.8% 0.006 264.531)",
        },
      },
      card: {
        backgroundColor: "#1d2331",
        foregroundColor: "oklch(92.8% 0.006 264.531)",
        borderColor: "#374151", // gray-700
        borderWidth: "1px",
        shadow: "0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)",
        textButton: {
          hoverBackgroundColor: "#374151",
          hoverForegroundColor: "oklch(92.8% 0.006 264.531)",
        },
      },
      textBlock: {
        background: "linear-gradient(116.26deg, #153AF0 43.88%, #528FFF 100%)",
      },
      primaryButton: {
        backgroundColor: "#153AF0", // coeff-blue-700
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#153AF0").lighten(0.8).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#7C3AED", // violet-600 - darker for better contrast with white
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#7C3AED").lighten(1.1).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#E5E7EB",
      defaultBackgroundColor: "rgba(129, 140, 248, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#3B82F6", // blue-500
        "#34D399", // emerald-400
        "#FBBF24", // amber-400
        "#F87171", // red-400
        "#22D3EE", // cyan-400
        "#F472B6", // pink-400
        "#818CF8", // indigo-400
        "#FB923C", // orange-400
        "#A78BFA", // violet-400
        "#4ADE80", // green-400
        "#2DD4BF", // teal-400
        "#C084FC", // purple-400
      ],
      gridColor: "#374151",
      gridBorderColor: "#4B5563",
      fontFamily: "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1F2937",
      tooltipTextColor: "#F9FAFB",
      tooltipBorderColor: "#4B5563",
      legendTextColor: "#E5E7EB",
      axisTickColor: "#9CA3AF",
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      barBackgroundOpacity: 0.7,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.darkTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.darkTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "Google Sans Flex, Inter, system-ui, -apple-system, sans-serif",
        backgroundColor: "#1d2331",
        foregroundColor: "#F9FAFB",
        borderColor: "#374151",
        headerBackgroundColor: "#111827",
        headerTextColor: "#F9FAFB",
        accentColor: "#818CF8",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Ocean Theme
  // ================================
  {
    themeId: "ocean",
    themeName: "Ocean",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "'Fira Sans', system-ui, sans-serif",
        borderRadius: "0.5rem",
        pageBackground:
          "linear-gradient(225deg, rgba(224, 254, 236, 1) 0%, rgba(199, 234, 255, 1) 50%) fixed",
        cardBackgroundGradients: [
          "from-cyan-600 to-blue-700",
          "from-sky-600 to-indigo-700",
          "from-teal-600 to-cyan-700",
          "from-blue-600 to-purple-700",
          "from-cyan-500 to-violet-700",
          "from-sky-500 to-blue-700",
        ],
      },
      main: {
        backgroundColor: "rgb(199, 234, 255)",
        foregroundColor: "#0C4A6E",
        mutedForegroundColor: "#0369A1",
        borderColor: "#BAE6FD",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(0, 0, 0, 0.08)",
          hoverForegroundColor: "#0C4A6E",
        },
      },
      nav: {
        backgroundColor: "oklch(98.4% 0.014 180.72)",
        foregroundColor: "#0C4A6E",
        borderColor: "oklch(95.3% 0.051 180.801)",
        shadow: "0 1px 3px 0 rgb(14 165 233 / 0.08), 0 1px 2px -1px rgb(14 165 233 / 0.06)",
        textButton: {
          hoverBackgroundColor: "rgba(0, 0, 0, 0.08)",
          hoverForegroundColor: "#0C4A6E",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        foregroundColor: "#0C4A6E",
        borderColor: "transparent",
        shadow: "0 4px 6px -1px rgb(14 165 233 / 0.1), 0 2px 4px -2px rgb(14 165 233 / 0.1)",
        textButton: {
          hoverBackgroundColor: "#E0F2FE",
          hoverForegroundColor: "#0C4A6E",
        },
      },
      textBlock: {
        background: "linear-gradient(118.38deg, #036AA2 36.8%, #6091E8 98.91%)",
      },
      primaryButton: {
        backgroundColor: "#0369A1", // sky-700 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: "#075985", // sky-800 - darker hover for good contrast
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#0E7490", // cyan-700 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: "#155E75", // cyan-800 - darker hover for good contrast
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#0C4A6E",
      defaultBackgroundColor: "rgba(14, 165, 233, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#0EA5E9", // sky-500 (primary)
        "#F97316", // orange-500 (contrast)
        "#8B5CF6", // violet-500
        "#10B981", // emerald-500
        "#EC4899", // pink-500
        "#EAB308", // yellow-500
        "#06B6D4", // cyan-500
        "#EF4444", // red-500
        "#22C55E", // green-500
        "#6366F1", // indigo-500
        "#14B8A6", // teal-500
        "#F59E0B", // amber-500
      ],
      gridColor: "#BAE6FD",
      gridBorderColor: "#7DD3FC",
      fontFamily: "'Fira Sans', system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#0C4A6E",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#0369A1",
      legendTextColor: "#0C4A6E",
      axisTickColor: "#0369A1",
      lineTension: 0.4,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 6,
      barBackgroundOpacity: 0.6,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.lightTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.lightTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "'Fira Sans', system-ui, sans-serif",
        accentColor: "#0EA5E9",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Slate Theme - Gray/purple glassmorphism
  // ================================
  {
    themeId: "slate",
    themeName: "Slate",
    logoVariant: "light_text",
    tailwind: {
      general: {
        fontFamily: "Raleway, 'Segoe UI', system-ui, sans-serif",
        borderRadius: "1rem",
        pageBackground: "linear-gradient(160deg, #3F3F5A 0%, #4A4A68 50%, #525270 100%) fixed", // slate-purple gradient
        cardBackgroundGradients: [
          "from-purple-700 to-indigo-800",
          "from-violet-700 to-purple-900",
          "from-violet-700 to-indigo-900",
          "from-indigo-700 to-purple-800",
          "from-purple-600 to-violet-800",
          "from-slate-700 to-purple-800",
        ],
        isDarkTheme: true,
      },
      main: {
        backgroundColor: "#3F3F5A", // muted purple-gray
        foregroundColor: "#F8FAFC", // slate-50
        mutedForegroundColor: "oklch(87.1% 0.006 286.286)", // zinc-300
        borderColor: "#52525B", // zinc-600
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(82, 82, 112, 0.95)", // slightly lighter slate-purple,
          hoverForegroundColor: "#F8FAFC",
        },
      },
      nav: {
        backgroundColor: "rgba(74, 74, 104, 0.98)", // slate-purple with transparency
        foregroundColor: "#F8FAFC",
        borderColor: "#52525B",
        shadow: "0 1px 4px 0 rgb(0 0 0 / 0.15), 0 1px 2px -1px rgb(0 0 0 / 0.15)",
        textButton: {
          hoverBackgroundColor: "rgba(82, 82, 112, 0.95)", // slightly lighter slate-purple,
          hoverForegroundColor: "#F8FAFC",
        },
      },
      card: {
        backgroundColor: "rgba(82, 81, 110, 0.95)",
        foregroundColor: "#F8FAFC",
        borderColor: "rgba(255, 255, 255, 0.1)", // subtle glass border
        borderWidth: "1px",
        shadow: "0 4px 6px -1px rgb(0 0 0 / 0.2), 0 2px 4px -2px rgb(0 0 0 / 0.2)",
        textButton: {
          hoverBackgroundColor: "#525270",
          hoverForegroundColor: "#F8FAFC",
        },
      },
      textBlock: {
        background: "linear-gradient(115.68deg, #9B4DD4 42.82%, #CD8DFC 98.85%)",
      },
      primaryButton: {
        backgroundColor: "#9b4dd4",
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#9b4dd4").lighten(0.9).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#854D0E", // yellow-800 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#854D0E").lighten(1.15).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#F8FAFC",
      defaultBackgroundColor: "rgba(168, 85, 247, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#FACC15", // yellow-400
        "#A855F7", // purple-500
        "#22D3EE", // cyan-400
        "#F472B6", // pink-400
        "#34D399", // emerald-400
        "#FB923C", // orange-400
        "#60A5FA", // blue-400
        "#F87171", // red-400
        "#A3E635", // lime-400
        "#E879F9", // fuchsia-400
        "#2DD4BF", // teal-400
        "#FBBF24", // amber-400
      ],
      gridColor: "#484858",
      gridBorderColor: "#71717A",
      fontFamily: "Raleway, 'Segoe UI', system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#3F3F5A",
      tooltipTextColor: "#F8FAFC",
      tooltipBorderColor: "#71717A",
      legendTextColor: "#F8FAFC",
      axisTickColor: "#A1A1AA",
      lineTension: 0.4,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 6,
      barBackgroundOpacity: 0.7,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.darkTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.darkTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "Raleway, 'Segoe UI', system-ui, sans-serif",
        backgroundColor: "#4A4A68",
        foregroundColor: "#F8FAFC",
        borderColor: "#52525B",
        headerBackgroundColor: "#3F3F5A",
        headerTextColor: "#F8FAFC",
        oddRowBackgroundColor: "#3F3F5A",
        accentColor: "#A855F7",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Midnight Theme - Deep purple/blue dark theme
  // ================================
  {
    themeId: "midnight",
    themeName: "Midnight",
    logoVariant: "light_text",
    tailwind: {
      general: {
        fontFamily: "'Funnel Display', system-ui, sans-serif",
        borderRadius: "0.75rem", // rounded-xl
        pageBackground: "linear-gradient(135deg, #0F0F1A 0%, #1A1A2E 40%, #252538 100%) fixed", // deep purple gradient
        cardBackgroundGradients: [
          "from-sky-700 to-cyan-400",
          "from-indigo-800 to-sky-900",
          "from-sky-800 to-indigo-950",
          "from-blue-800 to-indigo-950",
        ],
        isDarkTheme: true,
      },
      main: {
        backgroundColor: "#0F0F1A", // very dark blue-purple
        foregroundColor: "oklch(92.9% 0.013 255.508)", // slate-200
        mutedForegroundColor: "#94A3B8", // slate-400
        borderColor: "#2D2D44", // muted purple border
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(255, 245, 255, 0.14)",
          hoverForegroundColor: "oklch(92.9% 0.013 255.508)",
        },
      },
      nav: {
        backgroundColor: "rgba(26, 26, 46, 0.98)", // dark purple surface with transparency
        foregroundColor: "oklch(92.9% 0.013 255.508)",
        borderColor: "#2D2D44",
        shadow: "0 1px 4px 0 rgb(79 70 229 / 0.15), 0 1px 2px -1px rgb(0 0 0 / 0.2)",
        textButton: {
          hoverBackgroundColor: "rgba(255, 245, 255, 0.14)",
          hoverForegroundColor: "oklch(92.9% 0.013 255.508)",
        },
      },
      card: {
        backgroundColor: "rgba(40, 40, 61, 0.98)",
        foregroundColor: "oklch(92.9% 0.013 255.508)",
        borderColor: "#3D3D5C", // slightly lighter for visibility
        borderWidth: "1px",
        shadow: "0 0 6px 4px rgb(79 70 229 / 0.15)",
        textButton: {
          hoverBackgroundColor: "rgba(255, 255, 255, 0.1)",
          hoverForegroundColor: "oklch(92.9% 0.013 255.508)",
        },
      },
      textBlock: {
        background: "linear-gradient(115.73deg, #047857 41.4%, #59C79A 98.64%)",
      },
      primaryButton: {
        backgroundColor: "#4f46e5",
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#4f46e5").lighten(0.87).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#4F46E5", // indigo-600 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#4F46E5").lighten(1.1).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#E2E8F0",
      defaultBackgroundColor: "rgba(139, 92, 246, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#8B5CF6", // violet-500 (primary)
        "#22D3EE", // cyan-400
        "#F472B6", // pink-400
        "#34D399", // emerald-400
        "#FBBF24", // amber-400
        "#60A5FA", // blue-400
        "#FB923C", // orange-400
        "#A3E635", // lime-400
        "#F87171", // red-400
        "#2DD4BF", // teal-400
        "#E879F9", // fuchsia-400
        "#38BDF8", // sky-400
      ],
      gridColor: "#3A3A59",
      gridBorderColor: "#3D3D5C",
      fontFamily: "'Funnel Display', system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1A1A2E",
      tooltipTextColor: "#F8FAFC",
      tooltipBorderColor: "#3D3D5C",
      legendTextColor: "#E2E8F0",
      axisTickColor: "#94A3B8",
      lineTension: 0.4,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      barBackgroundOpacity: 0.6,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.darkTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.darkTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "'Funnel Display', system-ui, sans-serif",
        backgroundColor: "#252538",
        foregroundColor: "#F8FAFC",
        borderColor: "#2D2D44",
        headerBackgroundColor: "#1A1A2E",
        headerTextColor: "#F8FAFC",
        oddRowBackgroundColor: "#1A1A2E",
        accentColor: "#8B5CF6",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Mint Theme
  // ================================
  {
    themeId: "mint",
    themeName: "Mint",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "'Varela Round', Nunito, 'Segoe UI', Roboto, sans-serif",
        borderRadius: "1rem", // rounded-2xl
        cardBackgroundGradients: [
          "from-emerald-600 to-teal-700",
          "from-green-600 to-emerald-700",
          "from-teal-600 to-green-800",
          "from-emerald-500 to-green-700",
          "from-teal-500 to-cyan-700",
          "from-green-600 to-teal-800",
        ],
        // Soft diagonal gradient bands (fixed position)
        pageBackground: `
          linear-gradient(
            -50deg,
            transparent 0%,
            transparent 30%,
            rgba(16, 185, 129, 0.12) 45%,
            rgba(16, 185, 129, 0.05) 55%,
            transparent 65%,
            transparent 100%
          ) fixed,
          linear-gradient(
            -50deg,
            transparent 0%,
            transparent 50%,
            rgba(20, 184, 166, 0.10) 62%,
            rgba(20, 184, 166, 0.04) 72%,
            transparent 80%,
            transparent 100%
          ) fixed,
          linear-gradient(
            -50deg,
            transparent 0%,
            transparent 65%,
            rgba(16, 185, 129, 0.13) 78%,
            rgba(16, 185, 129, 0.05) 88%,
            transparent 95%,
            transparent 100%
          ) fixed,
          linear-gradient(
            130deg,
            rgba(167, 243, 208, 0.25) 0%,
            rgba(209, 250, 229, 0.15) 25%,
            transparent 50%
          ) fixed,
          #ECFDF5
        `,
      },
      main: {
        backgroundColor: "#ECFDF5",
        foregroundColor: "#063126",
        mutedForegroundColor: "#6B7280", // gray-500
        borderColor: "#D1FAE5", // emerald-100
        shadow: "none",
        textButton: {
          hoverBackgroundColor: Color.parse("#047857").lighten(1.8).saturate(0.9).toHexString(),
          hoverForegroundColor: "#064E3B",
        },
      },
      nav: {
        backgroundColor: "rgba(255, 255, 255, 0.95)", // light white with transparency
        foregroundColor: "#064E3B",
        borderColor: "#D1FAE5",
        shadow: "0 1px 3px 0 rgb(16 185 129 / 0.08), 0 1px 2px -1px rgb(16 185 129 / 0.06)",
        textButton: {
          hoverBackgroundColor: Color.parse("#047857").lighten(1.8).saturate(0.9).toHexString(),
          hoverForegroundColor: "#064E3B",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        foregroundColor: "#064E3B",
        borderColor: "transparent",
        shadow: "0 4px 6px -1px rgb(16 185 129 / 0.1), 0 2px 4px -2px rgb(16 185 129 / 0.1)",
        textButton: {
          hoverBackgroundColor: "#D1FAE5",
          hoverForegroundColor: "#064E3B",
        },
      },
      textBlock: {
        background: "linear-gradient(118.38deg, #047857 35.56%, #59C79A 98.08%)",
      },
      primaryButton: {
        backgroundColor: "#047857", // emerald-700 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: "#065F46", // emerald-800 - darker hover for good contrast
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#7C3AED", // violet-600 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#7C3AED").lighten(1.1).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#064E3B",
      defaultBackgroundColor: "rgba(16, 185, 129, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#10B981", // emerald-500 (primary)
        "#8B5CF6", // violet-500
        "#F472B6", // pink-400
        "#3B82F6", // blue-500
        "#F59E0B", // amber-500
        "#EF4444", // red-500
        "#06B6D4", // cyan-500
        "#F97316", // orange-500
        "#6366F1", // indigo-500
        "#EC4899", // pink-500
        "#14B8A6", // teal-500
        "#EAB308", // yellow-500
      ],
      gridColor: "#D1FAE5",
      gridBorderColor: "#A7F3D0",
      fontFamily: "'Varela Round', Nunito, 'Segoe UI', Roboto, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#064E3B",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#065F46",
      legendTextColor: "#064E3B",
      axisTickColor: "#6B7280",
      lineTension: 0.4,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 7,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 6,
      barBackgroundOpacity: 0.7,
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "'Varela Round', Nunito, 'Segoe UI', Roboto, sans-serif",
        accentColor: "#10B981",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Warm Theme - Cream/beige with orange accents
  // ================================
  {
    themeId: "warm",
    themeName: "Warm",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "Merriweather, Georgia, serif",
        borderRadius: "0.375rem",
        cardBackgroundGradients: ["from-yellow-700 to-orange-500", "from-orange-500 to-amber-500"],
      },
      main: {
        backgroundColor: "#E8E4DF", // warm gray-beige
        foregroundColor: "#1C1917", // stone-900
        mutedForegroundColor: "#57534E", // stone-600 - darker for better contrast
        borderColor: "#D6D3D1", // stone-300
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(214, 211, 209, 0.95)", // stone-300 with transparency,
          hoverForegroundColor: "#1C1917",
        },
      },
      nav: {
        backgroundColor: "rgba(231, 229, 228, 0.98)", // stone-200 with transparency
        foregroundColor: "#1C1917",
        borderColor: "#D6D3D1",
        shadow: "0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.04)",
        textButton: {
          hoverBackgroundColor: "rgba(214, 211, 209, 0.95)", // stone-300 with transparency,
          hoverForegroundColor: "#1C1917",
        },
      },
      card: {
        backgroundColor: "rgba(245, 245, 244, 0.9)", // stone-100
        foregroundColor: "#1C1917",
        borderColor: "transparent",
        shadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        textButton: {
          hoverBackgroundColor: "#E7E5E4",
          hoverForegroundColor: "#1C1917",
        },
      },
      textBlock: {
        background: "linear-gradient(114.01deg, #B6977E 37.66%, #D9C0AC 100%)",
      },
      primaryButton: {
        backgroundColor: "#C2410C", // orange-700 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: "#9A3412", // orange-800 - darker hover for good contrast
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#57534E", // stone-600 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#57534E").lighten(1.15).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#1C1917",
      defaultBackgroundColor: "rgba(234, 88, 12, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#EA580C", // orange-600 (primary)
        "#0D9488", // teal-600
        "#7C3AED", // violet-600
        "#2563EB", // blue-600
        "#DC2626", // red-600
        "#16A34A", // green-600
        "#DB2777", // pink-600
        "#0891B2", // cyan-600
        "#CA8A04", // yellow-600
        "#4F46E5", // indigo-600
        "#059669", // emerald-600
        "#9333EA", // purple-600
      ],
      gridColor: "#D6D3D1",
      gridBorderColor: "#A8A29E",
      fontFamily: "Merriweather, Georgia, serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1C1917",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#57534E",
      legendTextColor: "#1C1917",
      axisTickColor: "#78716C",
      lineTension: 0.2,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 3,
      barBackgroundOpacity: 0.7,
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Merriweather, Georgia, serif",
        accentColor: "#EA580C",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Honey Theme
  // ================================
  {
    themeId: "honey",
    themeName: "Honey",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "Lexend, system-ui, sans-serif",
        borderRadius: "0.8rem",
        pageBackground:
          "repeating-linear-gradient(45deg, rgba(252, 211, 77, 0.2) 0, rgba(252, 211, 77, 0.2) 2px, #FFFBEB 0, #FFFBEB 50%) 0 0 / 10px 10px fixed, #fff",
        cardBackgroundGradients: [
          "from-amber-400 to-yellow-700",
          "from-yellow-600 to-amber-500",
          "from-orange-400 to-amber-400",
          "from-yellow-500 to-orange-400",
          "from-amber-600 to-orange-400",
        ],
      },
      main: {
        backgroundColor: "#FFFBEB", // amber-50
        foregroundColor: "oklch(37.4% 0.01 67.558)", // stone-700
        mutedForegroundColor: "#6B7280", // gray-500
        borderColor: "#FDE68A", // amber-200
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(253, 230, 138, 0.95)", // amber-200 with transparency,
          hoverForegroundColor: "oklch(37.4% 0.01 67.558)",
        },
      },
      nav: {
        backgroundColor: "rgba(254, 243, 199, 0.98)", // amber-100 with transparency
        foregroundColor: "oklch(37.4% 0.01 67.558)",
        borderColor: "#FDE68A",
        shadow: "0 1px 3px 0 rgb(245 158 11 / 0.1), 0 1px 2px -1px rgb(245 158 11 / 0.08)",
        textButton: {
          hoverBackgroundColor: "rgba(253, 230, 138, 0.95)", // amber-200 with transparency,
          hoverForegroundColor: "oklch(37.4% 0.01 67.558)",
        },
      },
      card: {
        backgroundColor: "#FFFFFF",
        foregroundColor: "oklch(37.4% 0.01 67.558)",
        borderColor: "#FCD34D", // amber-300 (bolder border)
        borderWidth: "1.5px",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "#FEF3C7",
          hoverForegroundColor: "oklch(37.4% 0.01 67.558)",
        },
      },
      textBlock: {
        background: "linear-gradient(115.19deg, #FFEDA6 35.8%, #FFF8DE 98.31%)",
        foregroundColor: "#1F2937",
        border: "1px solid #FBC024",
      },
      primaryButton: {
        backgroundColor: "#FBBF24", // amber-400 (lighter)
        foregroundColor: "#1F2937",
        hoverBackgroundColor: Color.parse("#FBBF24").saturate(1.2).lighten(0.95).toHexString(),
        hoverForegroundColor: "#1F2937",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#1F2937", // gray-800
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#1F2937").lighten(0.5).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#1F2937",
      defaultBackgroundColor: "rgba(245, 158, 11, 0.15)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#F59E0B", // amber-500 (primary)
        "#3B82F6", // blue-500
        "#10B981", // emerald-500
        "#EF4444", // red-500
        "#8B5CF6", // violet-500
        "#EC4899", // pink-500
        "#06B6D4", // cyan-500
        "#F97316", // orange-500
        "#22C55E", // green-500
        "#6366F1", // indigo-500
        "#14B8A6", // teal-500
        "#A855F7", // purple-500
      ],
      gridColor: "#FDE68A",
      gridBorderColor: "#FCD34D",
      fontFamily: "Lexend, system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1F2937",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#4B5563",
      legendTextColor: "#1F2937",
      axisTickColor: "#6B7280",
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 6,
      barBackgroundOpacity: 1.0,
      plugins: [
        createBarGradientPlugin({
          startLuminosity: 1.2,
          endLuminosity: 1.0,
          startOpacity: 1.0,
          endOpacity: 1.0,
          gradientEndPosition: 0.4,
        }),
        createLineFillGradientPlugin({
          topOpacity: 1.0,
          bottomOpacity: 0.5,
          topLuminosity: 1.0,
          bottomLuminosity: 1.2,
          gradientEndPosition: 0.6,
        }),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Lexend, system-ui, sans-serif",
        accentColor: "#F59E0B",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Corporate Theme
  // ================================
  {
    themeId: "corporate",
    themeName: "Corporate",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "'Noticia Text', Georgia, serif",
        borderRadius: "0.25rem", // rounded (more conservative)
        cardBackgroundGradients: [
          "from-blue-700 to-indigo-800",
          "from-indigo-700 to-blue-900",
          "from-slate-700 to-blue-800",
          "from-blue-800 to-slate-900",
          "from-indigo-800 to-slate-900",
          "from-gray-700 to-blue-800",
        ],
      },
      main: {
        backgroundColor: "#F8FAFC", // slate-50
        foregroundColor: "#1E293B", // slate-800
        mutedForegroundColor: "#64748B", // slate-500
        borderColor: "#E2E8F0", // slate-200
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(226, 232, 240, 0.97)", // slate-200 with transparency,
          hoverForegroundColor: "#1E293B",
        },
      },
      nav: {
        backgroundColor: "rgba(241, 245, 249, 0.98)", // slate-100 with transparency
        foregroundColor: "#1E293B",
        borderColor: "#E2E8F0",
        shadow: "0 1px 2px 0 rgb(0 0 0 / 0.04), 0 1px 1px -1px rgb(0 0 0 / 0.03)",
        textButton: {
          hoverBackgroundColor: "rgba(226, 232, 240, 0.97)", // slate-200 with transparency,
          hoverForegroundColor: "#1E293B",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        foregroundColor: "#1E293B",
        borderColor: "#CBD5E1", // slate-300 (slightly darker for professional look)
        borderWidth: "1px",
        shadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        textButton: {
          hoverBackgroundColor: "#F1F5F9",
          hoverForegroundColor: "#1E293B",
        },
      },
      textBlock: {
        background: "linear-gradient(117.66deg, #1F40B0 41.01%, #4F78D3 96.84%)",
      },
      primaryButton: {
        backgroundColor: "#1E40AF", // blue-800
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#1E40AF").lighten(0.8).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#3730A3", // indigo-800
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#3730A3").lighten(0.8).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#1E293B",
      defaultBackgroundColor: "rgba(30, 64, 175, 0.1)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#1E40AF", // blue-800 (primary)
        "#EA580C", // orange-600
        "#059669", // emerald-600
        "#DC2626", // red-600
        "#7C3AED", // violet-600
        "#0891B2", // cyan-600
        "#CA8A04", // yellow-600
        "#DB2777", // pink-600
        "#16A34A", // green-600
        "#4F46E5", // indigo-600
        "#0D9488", // teal-600
        "#9333EA", // purple-600
      ],
      gridColor: "#E2E8F0",
      gridBorderColor: "#CBD5E1",
      fontFamily: "'Noticia Text', Georgia, serif",
      fontSize: 12.5,
      tooltipBackgroundColor: "#1E293B",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#475569",
      legendTextColor: "#1E293B",
      axisTickColor: "#64748B",
      lineTension: 0.2,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 1,
      barBorderRadius: 0,
      barBackgroundOpacity: 0.2,
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "'Noticia Text', Georgia, serif",
        accentColor: "#1E40AF",
        fontSize: 12.5,
        headerFontSize: 12,
        headerFontWeight: 800,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },
];

// ================================
// EXPERIMENTAL THEMES
// ================================

export const otherExperimentalThemes: Theme[] = [
  // ================================
  // Ultraviolet Theme - Bold purple background with clean white cards
  // ================================
  {
    themeId: "ultraviolet",
    themeName: "Ultraviolet",
    logoVariant: "light_logo_and_text",
    tailwind: {
      general: {
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
        borderRadius: "0.625rem",
        pageBackground: "linear-gradient(180deg, #6B4FE7 0%, #7A5FF3 50%, #8A6FF5 100%) fixed",
        cardBackgroundGradients: [
          "from-violet-500 to-purple-700",
          "from-purple-500 to-indigo-700",
          "from-fuchsia-500 to-violet-700",
          "from-indigo-500 to-purple-700",
          "from-violet-600 to-fuchsia-700",
          "from-purple-600 to-violet-800",
        ],
      },
      // White floating container with rounded corners
      dashboardContainer: {
        background: "#FFFFFF",
        borderRadius: "1.5rem",
        shadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
      },
      main: {
        backgroundColor: "#FFFFFF", // White to match dashboardContainer
        foregroundColor: "#374151", // gray-700 - dark for white dashboardContainer
        mutedForegroundColor: "#4B5563", // gray-600 - darker for better contrast on white
        borderColor: "#E5E7EB",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "#F3F4F6",
          hoverForegroundColor: "#374151",
        },
      },
      nav: {
        backgroundColor: Color.parse("#6B4FE7").lighten(0.95).toHexString(),
        foregroundColor: "#ffffff", // pure white for better contrast
        borderColor: Color.parse("#6B4FE7").lighten(0.9).toHexString(),
        shadow: "0 2px 8px 0 rgb(0 0 0 / 0.15)",
        textButton: {
          hoverBackgroundColor: Color.parse("#6B4FE7").lighten(0.8).toHexString(),
          hoverForegroundColor: "#ffffff",
        },
      },
      card: {
        backgroundColor: "#F3F4F6", // gray-100 - distinct from white container
        foregroundColor: "#374151",
        mutedForegroundColor: "#4B5563", // gray-600 - darker for better contrast on gray-100
        borderColor: "transparent",
        borderWidth: "0px",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "#E5E7EB", // gray-200 - visible hover state
          hoverForegroundColor: "#1F2937",
        },
      },
      primaryButton: {
        backgroundColor: Color.parse("#6B4FE7").lighten(0.75).toHexString(),
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#6B4FE7").lighten(0.85).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#C2410C", // orange-700 - darker for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: "#9A3412", // orange-800 - darker hover for good contrast
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#374151",
      defaultBackgroundColor: "rgba(249, 115, 22, 0.85)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#F97316", // orange-500 (primary - matches bar chart)
        "#3B82F6", // blue-500 (pie chart blue)
        "#FBBF24", // amber-400 (pie chart yellow)
        "#FB7185", // rose-400 (pie chart coral)
        "#8B5CF6", // violet-500
        "#10B981", // emerald-500
        "#EC4899", // pink-500
        "#06B6D4", // cyan-500
        "#EF4444", // red-500
        "#84CC16", // lime-500
        "#14B8A6", // teal-500
        "#F59E0B", // amber-500
      ],
      gridColor: "#E5E7EB",
      gridBorderColor: "#D1D5DB",
      fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1F2937",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#374151",
      legendTextColor: "#374151",
      axisTickColor: "#6B7280",
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      barBackgroundOpacity: 0.85,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.lightTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.lightTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
        accentColor: "#7C5CFC",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 600,
        spacing: 4,
        borderRadius: 6,
        wrapperBorderRadius: 6,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Golden Harvest Theme
  // ================================
  {
    themeId: "golden-harvest",
    themeName: "Golden Harvest",
    logoVariant: "dark",
    tailwind: {
      general: {
        fontFamily: "Poppins, 'Plus Jakarta Sans', system-ui, sans-serif",
        borderRadius: "0.5rem",
        pageBackground: "linear-gradient(135deg, #FFF8E7 0%, #FFFBEB 50%, #FEF3C7 100%) fixed",
        cardBackgroundGradients: [
          "from-[#115E59] to-[#0D9488]",
          "from-[#115E59] to-[#065F46]",
          "from-[#0F766E] to-[#134E4A]",
          "from-[#D97706] to-[#B45309]",
          "from-[#115E59] to-[#047857]",
          "from-[#059669] to-[#115E59]",
        ],
      },
      main: {
        backgroundColor: "#FFF8E7",
        foregroundColor: "#292524",
        mutedForegroundColor: "#78716C",
        borderColor: "#E7E5E4",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(17, 94, 89, 0.1)",
          hoverForegroundColor: "#292524",
        },
      },
      nav: {
        backgroundColor: "rgba(255, 251, 235, 0.98)",
        foregroundColor: "#292524",
        borderColor: "#E7E5E4",
        shadow: "0 1px 3px 0 rgb(41 37 36 / 0.06), 0 1px 2px -1px rgb(41 37 36 / 0.04)",
        textButton: {
          hoverBackgroundColor: "rgba(17, 94, 89, 0.1)",
          hoverForegroundColor: "#292524",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        foregroundColor: "#292524",
        borderColor: "#E7E5E4",
        borderWidth: "1px",
        shadow: "0 1px 4px 0 rgb(41 37 36 / 0.06), 0 1px 2px -1px rgb(41 37 36 / 0.04)",
        textButton: {
          hoverBackgroundColor: "#FEF3C7",
          hoverForegroundColor: "#292524",
        },
      },
      primaryButton: {
        backgroundColor: "#115E59",
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#115E59").lighten(1.2).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#92400E", // amber-800 - darker for better hover contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#92400E").lighten(1.2).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#292524",
      defaultBackgroundColor: "rgba(17, 94, 89, 0.85)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#115E59", // Teal-800 (primary forest green)
        "#D4A574", // Golden tan
        "#0D9488", // Teal-600
        "#EAB308", // Yellow-500 (gold)
        "#854D0E", // Yellow-800 (dark gold)
        "#059669", // Emerald-600
        "#B45309", // Amber-700
        "#14B8A6", // Teal-500
        "#CA8A04", // Yellow-600
        "#047857", // Emerald-700
        "#F59E0B", // Amber-500
        "#0F766E", // Teal-700
      ],
      gridColor: "#E7E5E4",
      gridBorderColor: "#D6D3D1",
      fontFamily: "Poppins, 'Plus Jakarta Sans', system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#115E59",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#0D9488",
      legendTextColor: "#44403C",
      axisTickColor: "#78716C",
      lineTension: 0.35,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      barBackgroundOpacity: 0.85,
      plugins: [
        createBarGradientPlugin({
          startLuminosity: 1.1,
          endLuminosity: 0.95,
          startOpacity: 0.8,
          endOpacity: 0.85,
          gradientEndPosition: 0.5,
        }),
        createLineFillGradientPlugin({
          topOpacity: 0.5,
          bottomOpacity: 0.1,
          topLuminosity: 1.05,
          bottomLuminosity: 1.15,
          gradientEndPosition: 0.5,
        }),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Poppins, 'Plus Jakarta Sans', system-ui, sans-serif",
        backgroundColor: "#FFFFFF",
        foregroundColor: "#292524",
        borderColor: "#E7E5E4",
        headerBackgroundColor: "#115E59",
        headerTextColor: "#FFFFFF",
        oddRowBackgroundColor: "#FFFBEB",
        accentColor: "#115E59",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 600,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Voxel Theme - Blocky pixel aesthetic
  // ================================
  {
    themeId: "voxel",
    themeName: "Voxel",
    logoVariant: "light_logo_and_text",
    tailwind: {
      general: {
        fontFamily: "Minecraft, 'Courier New', monospace",
        borderRadius: "0", // Everything is blocky - no rounded corners
        pageBackground: "#68afd7",
        cardBackgroundGradients: [
          "from-green-600 to-green-800", // Grass
          "from-cyan-400 to-cyan-600", // Diamond
          "from-red-600 to-red-800", // Redstone
          "from-yellow-500 to-amber-600", // Gold
          "from-blue-600 to-blue-800", // Lapis
          "from-emerald-500 to-emerald-700", // Emerald
        ],
        isDarkTheme: true,
      },
      dashboardContainer: {
        background: "rgba(42, 42, 42, 0.9)",
      },
      main: {
        backgroundColor: "rgb(42, 42, 42)",
        foregroundColor: "#FFFFFF",
        mutedForegroundColor: "#A0A0A0", // Stone gray
        borderColor: "#1A1A1A", // Dark border like inventory slots
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "#3C3C3C",
          hoverForegroundColor: "#FFFFFF",
          borderRadius: "0", // Blocky buttons
        },
      },
      nav: {
        backgroundColor: "rgb(38, 38, 38)", // Dark inventory gray
        foregroundColor: "#FFFFFF",
        borderColor: "#1A1A1A",
        shadow: "0 2px 0 0 #1A1A1A", // Hard shadow like pixel art
        textButton: {
          hoverBackgroundColor: Color.parse("#6BC438").lighten(0.9).toHexString(),
          hoverForegroundColor: "#1A1A1A",
          borderRadius: "0", // Blocky buttons
        },
      },
      card: {
        backgroundColor: "rgba(60, 60, 60, 0.95)", // Lighter inventory slot
        foregroundColor: "#FFFFFF",
        borderColor: "#1A1A1A",
        borderWidth: "2px", // Thick pixel-style border
        shadow: "4px 4px 0 0 #1A1A1A", // Hard drop shadow like pixel art
        textButton: {
          hoverBackgroundColor: "#4A4A4A",
          hoverForegroundColor: "#FFFFFF",
          borderRadius: "0", // Blocky buttons
        },
      },
      primaryButton: {
        backgroundColor: "#6BC438",
        foregroundColor: "#1A1A1A",
        hoverBackgroundColor: Color.parse("#6BC438").lighten(0.9).toHexString(),
        hoverForegroundColor: "#1A1A1A",
        shadow: "inset -3px -3px 0 0 #3D7A1E, inset 3px 3px 0 0 #7ED147, 3px 3px 0 0 #1A1A1A",
        borderColor: "#2D5A18",
      },
      secondaryButton: {
        backgroundColor: "#41E0D1", // Diamond cyan
        foregroundColor: "#1A1A1A", // Dark text on cyan
        hoverBackgroundColor: "#5EEADD",
        hoverForegroundColor: "#1A1A1A",
        shadow: "inset -3px -3px 0 0 #2BA99D, inset 3px 3px 0 0 #6FEFE3, 3px 3px 0 0 #1A1A1A",
        borderColor: "#1F7A70",
      },
    },
    chartjs: {
      defaultColor: "#FFFFFF",
      defaultBackgroundColor: "rgba(90, 171, 44, 0.8)", // Grass green
      defaultBorderColor: "#1A1A1A",
      colorPalette: [
        "#5AAB2C", // Grass green (primary)
        "#41E0D1", // Diamond cyan
        "#E52727", // Redstone red
        "#FCDB3D", // Gold yellow
        "#3555E3", // Lapis blue
        "#17DD62", // Emerald green
        "#FF7F00", // Pumpkin orange
        "#B041E0", // Amethyst purple
        "#1F8AFF", // Water blue
        "#FFFFFF", // Snow white
        "#7B5E4D", // Wood brown
        "#E3D4A0", // Sand beige
      ],
      gridColor: "#3C3C3C",
      gridBorderColor: "#4A4A4A",
      fontFamily: "Minecraft, 'Courier New', monospace",
      fontSize: 16,
      tooltipBackgroundColor: "#1A1A1A",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#5AAB2C",
      legendTextColor: "#FFFFFF",
      axisTickColor: "#A0A0A0",
      lineTension: 0, // Sharp angular lines - no curves!
      lineBorderWidth: 2, // Thicker lines for pixel look
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 2, // Visible border on bars
      barBorderRadius: 0, // Square bars - no rounding!
      barBackgroundOpacity: 0.9,
      plugins: [
        createBarGradientPlugin({
          startLuminosity: 1.0,
          endLuminosity: 0.8,
          startOpacity: 0.9,
          endOpacity: 0.95,
          gradientEndPosition: 1.0, // Full gradient
        }),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "Minecraft, 'Courier New', monospace",
        backgroundColor: "#3C3C3C",
        foregroundColor: "#FFFFFF",
        borderColor: "#1A1A1A",
        headerBackgroundColor: "#2D2D2D",
        headerTextColor: "#FFFFFF",
        oddRowBackgroundColor: "#2D2D2D",
        accentColor: "#5AAB2C", // Grass green
        fontSize: 16,
        headerFontSize: 16,
        headerFontWeight: 700,
        spacing: 4,
        borderRadius: 0, // Square everything
        wrapperBorderRadius: 0,
        cellHorizontalPaddingScale: 1.0,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 1.0,
      },
    },
    extraCss: voxelCss,
    setup: voxelSetup,
  },
];

const experimentalBrandThemes: Theme[] = [
  // ================================
  // Netflix
  // ================================
  {
    themeId: "netflix-brand",
    themeName: "Netflix",
    logoVariant: "light_text",
    customLogo: {
      src: "https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg",
      alt: "Netflix",
      height: 28,
    },
    tailwind: {
      general: {
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        borderRadius: "0.25rem",
        pageBackground: "linear-gradient(180deg, #141414 0%, #1A1A1A 50%, #0D0D0D 100%) fixed",
        cardBackgroundGradients: [
          "from-red-700 to-red-900",
          "from-red-600 to-black",
          "from-rose-700 to-red-950",
          "from-red-800 to-neutral-900",
          "from-red-700 to-zinc-900",
          "from-rose-800 to-black",
        ],
        isDarkTheme: true,
      },
      main: {
        backgroundColor: "#141414", // Netflix dark background
        foregroundColor: "#FFFFFF",
        mutedForegroundColor: "#A3A3A3",
        borderColor: "#333333",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(50, 50, 50, 0.95)",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      nav: {
        backgroundColor: "rgba(20, 20, 20, 0.98)",
        foregroundColor: "#FFFFFF",
        borderColor: "#333333",
        shadow: "0 1px 4px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.25)",
        textButton: {
          hoverBackgroundColor: "rgba(50, 50, 50, 0.95)",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      card: {
        backgroundColor: "rgba(35, 35, 35, 0.9)",
        foregroundColor: "#FFFFFF",
        borderColor: "#333333",
        borderWidth: "1px",
        shadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
        textButton: {
          hoverBackgroundColor: "#2A2A2A",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      primaryButton: {
        backgroundColor: "#E50914", // Netflix red
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#E50914").lighten(0.85).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#B20710", // Darker red
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#B20710").lighten(0.85).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#FFFFFF",
      defaultBackgroundColor: "rgba(229, 9, 20, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#E50914", // Netflix red (primary)
        "#46D369", // Netflix green (used in UI)
        "#FFB53E", // Gold/amber
        "#0080FF", // Blue
        "#FF6B6B", // Light red
        "#5FA8D3", // Sky blue
        "#9B59B6", // Purple
        "#B20710", // Dark red
        "#FFFFFF", // White
        "#F39C12", // Orange
        "#1ABC9C", // Teal
        "#E74C3C", // Coral red
      ],
      gridColor: "#333333",
      gridBorderColor: "#444444",
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1A1A1A",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#E50914",
      legendTextColor: "#FFFFFF",
      axisTickColor: "#A3A3A3",
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 2,
      barBackgroundOpacity: 0.8,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.darkTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.darkTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        backgroundColor: "#1A1A1A",
        foregroundColor: "#FFFFFF",
        borderColor: "#333333",
        headerBackgroundColor: "#141414",
        headerTextColor: "#FFFFFF",
        oddRowBackgroundColor: "#141414",
        accentColor: "#E50914",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 700,
        spacing: 4,
        borderRadius: 2,
        wrapperBorderRadius: 2,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Miro
  // ================================
  {
    themeId: "miro-brand",
    themeName: "Miro",
    logoVariant: "dark",
    customLogo: {
      src: "https://cdn.brandfetch.io/idAnDTFapY/theme/dark/logo.svg?c=1idb4uSoFAS93YHN-kW",
      alt: "Miro",
      height: 32,
    },
    tailwind: {
      general: {
        fontFamily: "Rubik, system-ui, sans-serif",
        borderRadius: "0.75rem",
        pageBackground:
          "radial-gradient(circle at 10px 10px, #DCDADC 1.2px, transparent 1.2px) 0 0 / 20px 20px fixed, rgb(240, 240, 240)",
        cardBackgroundGradients: [
          "from-yellow-400 to-lime-500",
          "from-indigo-400 to-teal-500",
          "from-sky-500 to-[#050038]",
        ],
      },
      main: {
        backgroundColor: "rgb(240, 240, 240)",
        foregroundColor: "#050038", // Miro dark blue text
        mutedForegroundColor: "#4B4B6B", // darker for better contrast
        borderColor: "#E6E6E6",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(255, 208, 47, 0.3)",
          hoverForegroundColor: "#050038",
        },
      },
      nav: {
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        foregroundColor: "#050038",
        borderColor: "#E6E6E6",
        shadow: "0 1px 3px 0 rgb(5 0 56 / 0.06), 0 1px 2px -1px rgb(5 0 56 / 0.04)",
        textButton: {
          hoverBackgroundColor: "rgba(255, 208, 47, 0.3)",
          hoverForegroundColor: "#050038",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        foregroundColor: "#050038",
        borderColor: "transparent",
        borderWidth: "1px",
        shadow: "0 4px 8px 0 rgb(5 0 56 / 0.06), 0 2px 4px -1px rgb(0 0 0 / 0.04)",
        textButton: {
          hoverBackgroundColor: "#FFF8E1",
          hoverForegroundColor: "#050038",
        },
      },
      primaryButton: {
        backgroundColor: "#FFD02F", // Miro yellow
        foregroundColor: "#050038", // Dark text on yellow
        hoverBackgroundColor: Color.parse("#FFD02F").saturate(1.7).lighten(0.95).toHexString(),
        hoverForegroundColor: "#050038",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#F2C94C",
        foregroundColor: "#050038",
        hoverBackgroundColor: Color.parse("#F2C94C").lighten(0.9).toHexString(),
        hoverForegroundColor: "#050038",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#050038",
      defaultBackgroundColor: "rgba(255, 208, 47, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#FFD02F", // Miro yellow (primary)
        "#2D9CDB", // Blue
        "#F24726", // Miro red
        "#12CDD4", // Miro cyan
        "#9B51E0", // Purple
        "#27AE60", // Green
        "#F2994A", // Orange
        "#EB5757", // Red
        "#56CCF2", // Light blue
        "#BB6BD9", // Light purple
        "#FF7F50", // Coral
        "#050038", // Miro dark blue (moved to end)
      ],
      gridColor: "#E6E6E6",
      gridBorderColor: "#D1D1D1",
      fontFamily: "Rubik, system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#050038",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#FFD02F",
      legendTextColor: "#050038",
      axisTickColor: "#6B6B8D",
      lineTension: 0.4,
      lineBorderWidth: 3,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 8,
      barBackgroundOpacity: 0.85,
      plugins: [
        createBarGradientPlugin(ChartjsPluginDefaultOptions.barGradient.lightTheme),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.lightTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Rubik, system-ui, sans-serif",
        backgroundColor: "#FFFFFF",
        foregroundColor: "#050038",
        borderColor: "#E6E6E6",
        headerBackgroundColor: "#FFF9E6",
        headerTextColor: "#050038",
        oddRowBackgroundColor: "#FFFDF5",
        accentColor: "#FFD02F",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 700,
        spacing: 4,
        borderRadius: 6,
        wrapperBorderRadius: 6,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Klaviyo
  // ================================
  {
    themeId: "klaviyo-brand",
    themeName: "Klaviyo",
    logoVariant: "light_text",
    customLogo: {
      src: "https://cdn.brandfetch.io/idOL_ClNhe/theme/light/logo.svg?c=1idb4uSoFAS93YHN-kW",
      alt: "Klaviyo",
      height: 28,
    },
    tailwind: {
      general: {
        fontFamily: "Quicksand, system-ui, sans-serif",
        borderRadius: "0.5rem",
        pageBackground: "linear-gradient(160deg, #1A2634 0%, #243447 50%, #1A2634 100%) fixed",
        cardBackgroundGradients: [
          "from-emerald-500 to-teal-700",
          "from-teal-500 to-cyan-700",
          "from-green-500 to-emerald-700",
          "from-cyan-500 to-teal-700",
          "from-emerald-600 to-green-800",
          "from-teal-600 to-emerald-800",
        ],
        isDarkTheme: true,
      },
      main: {
        backgroundColor: "#1A2634", // Klaviyo dark blue-gray
        foregroundColor: "#FFFFFF",
        mutedForegroundColor: "#A0AEC0",
        borderColor: "#3D4F5F",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(0, 212, 170, 0.15)",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      nav: {
        backgroundColor: "rgba(26, 38, 52, 0.98)",
        foregroundColor: "#FFFFFF",
        borderColor: "#3D4F5F",
        shadow: "0 1px 4px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.15)",
        textButton: {
          hoverBackgroundColor: "rgba(0, 212, 170, 0.15)",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      card: {
        backgroundColor: "rgba(45, 62, 78, 0.85)",
        foregroundColor: "#FFFFFF",
        borderColor: "#3D4F5F",
        borderWidth: "1px",
        shadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
        textButton: {
          hoverBackgroundColor: "#2D3E4E",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      primaryButton: {
        backgroundColor: "#00D4AA", // Klaviyo green
        foregroundColor: "#1A2634", // Dark text on green
        hoverBackgroundColor: Color.parse("#00D4AA").lighten(0.87).toHexString(),
        hoverForegroundColor: "#1A2634",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#00B894",
        foregroundColor: "#1A2634",
        hoverBackgroundColor: Color.parse("#00B894").lighten(1.1).toHexString(), // Slightly brighter
        hoverForegroundColor: "#1A2634",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#FFFFFF",
      defaultBackgroundColor: "rgba(0, 212, 170, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#00D4AA", // Klaviyo green (primary)
        "#6C5CE7", // Purple
        "#FF6B6B", // Coral
        "#FFE66D", // Yellow
        "#A8E6CF", // Light green
        "#DDA0DD", // Plum
        "#87CEEB", // Sky blue
        "#F7DC6F", // Light yellow
        "#4ECDC4", // Teal
        "#BB8FCE", // Light purple
        "#00B4D8", // Cyan
        "#FF8C42", // Orange
      ],
      gridColor: "#3D4F5F",
      gridBorderColor: "#4A5D6F",
      fontFamily: "Quicksand, system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#1A2634",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#00D4AA",
      legendTextColor: "#FFFFFF",
      axisTickColor: "#A0AEC0",
      lineTension: 0.35,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 4,
      barBackgroundOpacity: 0.8,
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "Quicksand, system-ui, sans-serif",
        backgroundColor: "#243447",
        foregroundColor: "#FFFFFF",
        borderColor: "#3D4F5F",
        headerBackgroundColor: "#1A2634",
        headerTextColor: "#FFFFFF",
        oddRowBackgroundColor: "#1A2634",
        accentColor: "#00D4AA",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 700,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Richemont
  // ================================
  {
    themeId: "richemont-brand",
    themeName: "Richemont",
    logoVariant: "dark",
    customLogo: {
      src: "https://cdn.brandfetch.io/idXhwPJp3W/theme/dark/logo.svg?c=1idb4uSoFAS93YHN-kW",
      alt: "Richemont",
      height: 16,
    },
    tailwind: {
      general: {
        fontFamily: "Merriweather, Georgia, serif",
        borderRadius: "0.25rem", // Minimal, refined corners
        pageBackground: "linear-gradient(180deg, #FFFFFF 0%, #F8F8F7 50%, #F5F5F4 100%) fixed",
        cardBackgroundGradients: ["from-[#A8996E] to-[#C9A962]", "from-[#004165] to-[#006994]"],
      },
      main: {
        backgroundColor: "rgb(245, 245, 244)",
        foregroundColor: "rgb(81, 84, 84)",
        mutedForegroundColor: "#4A4A4A", // Tundora gray
        borderColor: "#E8E8E8",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(0, 65, 101, 0.08)",
          hoverForegroundColor: "rgb(81, 84, 84)",
        },
      },
      nav: {
        backgroundColor: "rgba(251, 247, 235, 1)", // Gold tint
        foregroundColor: "rgb(81, 84, 84)",
        borderColor: "#E8E8E8",
        shadow: "0 1px 2px 0 rgb(0 0 0 / 0.04), 0 1px 1px -1px rgb(0 0 0 / 0.03)",
        textButton: {
          hoverBackgroundColor: "rgba(0, 65, 101, 0.08)",
          hoverForegroundColor: "rgb(81, 84, 84)",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        foregroundColor: "rgb(81, 84, 84)",
        borderColor: "#E8E8E8",
        borderWidth: "1px",
        shadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
        textButton: {
          hoverBackgroundColor: "#F0F0EF",
          hoverForegroundColor: "rgb(81, 84, 84)",
        },
      },
      primaryButton: {
        backgroundColor: "#004165", // Richemont deep blue
        foregroundColor: "#ffffff",
        hoverBackgroundColor: Color.parse("#004165").lighten(1.2).toHexString(),
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#6B5A45", // Even darker gold for better contrast
        foregroundColor: "#ffffff",
        hoverBackgroundColor: "#7A6B58", // Slightly lighter, manually chosen for contrast
        hoverForegroundColor: "#ffffff",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#1A1A1A",
      defaultBackgroundColor: "rgba(0, 65, 101, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#004165", // Richemont deep blue (primary)
        "#A8996E", // Sandal gold
        "#8B3A3A", // Indian red / burgundy
        "#556B2F", // Dark olive green
        "#5D4157", // Eggplant purple
        "#708090", // Slate gray
        "#C9A962", // Bright gold
        "#8B4513", // Saddle brown
        "#006994", // Cerulean blue
        "#4A4A4A", // Charcoal gray
        "#B87333", // Copper
        "#36454F", // Dark slate
      ],
      gridColor: "#E8E8E8",
      gridBorderColor: "#D8D8D8",
      fontFamily: "Merriweather, Georgia, serif",
      fontSize: 11,
      tooltipBackgroundColor: "#1A1A1A",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#004165",
      legendTextColor: "#1A1A1A",
      axisTickColor: "#4A4A4A",
      lineTension: 0.3,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 2,
      barBackgroundOpacity: 0.85,
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Merriweather, Georgia, serif",
        backgroundColor: "#FFFFFF",
        foregroundColor: "#1A1A1A",
        borderColor: "#E8E8E8",
        headerBackgroundColor: "#FAFAFA",
        headerTextColor: "#1A1A1A",
        oddRowBackgroundColor: "#FCFCFC",
        accentColor: "#004165",
        fontSize: 11,
        headerFontSize: 11,
        headerFontWeight: 600,
        spacing: 4,
        borderRadius: 2,
        wrapperBorderRadius: 2,
        cellHorizontalPaddingScale: 1,
        headerVerticalPaddingScale: 1.3,
        rowVerticalPaddingScale: 1,
      },
    },
  },

  // ================================
  // Spotify
  // ================================
  {
    themeId: "spotify-brand",
    themeName: "Spotify",
    logoVariant: "light_text",
    customLogo: {
      src: "https://cdn.brandfetch.io/id20mQyGeY/theme/light/logo.svg?c=1idb4uSoFAS93YHN-kW",
      alt: "Spotify",
      height: 32,
    },
    tailwind: {
      general: {
        fontFamily: "Nunito, 'Helvetica Neue', sans-serif",
        borderRadius: "0.5rem",
        pageBackground: "linear-gradient(180deg, #121212 0%, #0A0A0A 100%) fixed",
        cardBackgroundGradients: [
          "from-emerald-600 to-green-800",
          "from-green-500 to-emerald-800",
          "from-teal-600 to-green-900",
        ],
        isDarkTheme: true,
      },
      main: {
        backgroundColor: "#121212", // Spotify dark background
        foregroundColor: "#FFFFFF",
        mutedForegroundColor: "#A7A7A7",
        borderColor: "#282828",
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(40, 40, 40, 0.95)",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      nav: {
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        foregroundColor: "#FFFFFF",
        borderColor: "#282828",
        shadow: "0 1px 4px 0 rgb(0 0 0 / 0.35), 0 1px 2px -1px rgb(0 0 0 / 0.3)",
        textButton: {
          hoverBackgroundColor: "rgba(40, 40, 40, 0.95)",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      card: {
        backgroundColor: "rgba(24, 28, 24, 0.9)",
        foregroundColor: "#FFFFFF",
        borderColor: "#282828",
        shadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
        textButton: {
          hoverBackgroundColor: "#282828",
          hoverForegroundColor: "#FFFFFF",
        },
      },
      primaryButton: {
        backgroundColor: "#1ED760", // Spotify green
        foregroundColor: "#000000", // Black text on green
        hoverBackgroundColor: Color.parse("#1ED760").lighten(0.85).toHexString(),
        hoverForegroundColor: "#000000",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#1DB954", // Darker green
        foregroundColor: "#000000",
        hoverBackgroundColor: Color.parse("#1DB954").lighten(0.85).toHexString(),
        hoverForegroundColor: "#000000",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#FFFFFF",
      defaultBackgroundColor: "rgba(30, 215, 96, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#1ED760", // Spotify green (primary)
        "#F59B23", // Orange
        "#2E77D0", // Blue
        "#E91429", // Red
        "#8C67AB", // Purple
        "#1DB954", // Darker green
        "#509BF5", // Light blue
        "#FF6B6B", // Coral
        "#FFD93D", // Yellow
        "#148A08", // Forest green
        "#1E3264", // Navy
        "#E8115B", // Pink
      ],
      gridColor: "#282828",
      gridBorderColor: "#404040",
      fontFamily: "Nunito, 'Helvetica Neue', sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#282828",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#1ED760",
      legendTextColor: "#FFFFFF",
      axisTickColor: "#A7A7A7",
      lineTension: 0.4,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 5,
      barBackgroundOpacity: 0.7,
      plugins: [
        createBarGradientPlugin({
          startLuminosity: 0.6,
          endLuminosity: 1.0,
          startOpacity: 0.9,
          endOpacity: 0.9,
          gradientEndPosition: 0.8,
        }),
        createLineFillGradientPlugin(ChartjsPluginDefaultOptions.lineFillGradient.darkTheme),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "dark",
        fontFamily: "Nunito, 'Helvetica Neue', sans-serif",
        backgroundColor: "#121212",
        foregroundColor: "#FFFFFF",
        borderColor: "#282828",
        headerBackgroundColor: "#181818",
        headerTextColor: "#FFFFFF",
        oddRowBackgroundColor: "#181818",
        accentColor: "#1ED760",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 700,
        spacing: 4,
        borderRadius: 4,
        wrapperBorderRadius: 4,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
  },

  // ================================
  // Disney
  // ================================
  {
    themeId: "disney-brand",
    themeName: "Disney",
    logoVariant: "dark",
    customLogo: {
      src: "https://upload.wikimedia.org/wikipedia/commons/a/a4/Disney_wordmark.svg",
      alt: "Disney",
      height: 36,
    },
    tailwind: {
      general: {
        fontFamily: "Nunito, 'Varela Round', system-ui, sans-serif", // Rounded, friendly font
        borderRadius: "1rem", // Rounded, friendly corners
        pageBackground:
          // Magical light streaks with very faint rainbow tint
          "linear-gradient(145deg, transparent 0%, transparent 20%, rgba(255,245,248,0.25) 21%, rgba(255,254,245,0.3) 22%, rgba(248,255,250,0.25) 23%, rgba(248,252,255,0.3) 24%, rgba(252,248,255,0.25) 25%, transparent 26%, transparent 100%) fixed, " +
          "linear-gradient(110deg, transparent 0%, transparent 55%, rgba(255,245,248,0.3) 56%, rgba(255,254,245,0.35) 58%, rgba(248,255,250,0.3) 60%, rgba(248,252,255,0.35) 62%, rgba(252,248,255,0.3) 64%, transparent 66%, transparent 100%) fixed, " +
          "radial-gradient(ellipse at bottom right, rgba(255,248,220,0.6) 0%, transparent 50%) fixed, " +
          "radial-gradient(ellipse at top, #E8F4FF 0%, #F0F8FF 50%, #F8FCFF 100%) fixed",
        cardBackgroundGradients: [
          "from-[#01147C] to-[#113CCF]",
          "from-blue-700 to-indigo-800",
          "from-indigo-600 to-purple-700",
          "from-[#01147C] to-purple-800",
          "from-sky-600 to-[#01147C]",
          "from-violet-600 to-indigo-800",
        ],
      },
      main: {
        backgroundColor: "#F8F9FF", // Soft magical white-blue
        foregroundColor: "#1A1A2E", // Dark navy text
        mutedForegroundColor: "#5C5C7A", // Muted purple-gray
        borderColor: "#E8E8F0", // Light border
        shadow: "none",
        textButton: {
          hoverBackgroundColor: "rgba(1, 20, 124, 0.08)", // Blue tinted hover,
          hoverForegroundColor: "#1A1A2E",
        },
      },
      nav: {
        backgroundColor: "rgba(255, 255, 255, 0.98)", // Light nav
        foregroundColor: "#1A1A2E",
        borderColor: "#E8E8F0",
        shadow: "0 1px 3px 0 rgb(1 20 124 / 0.06), 0 1px 2px -1px rgb(1 20 124 / 0.04)",
        textButton: {
          hoverBackgroundColor: "rgba(1, 20, 124, 0.08)", // Blue tinted hover,
          hoverForegroundColor: "#1A1A2E",
        },
      },
      card: {
        backgroundColor: "rgba(255, 255, 255, 0.95)", // White cards
        foregroundColor: "#1A1A2E",
        borderColor: "transparent",
        shadow: "0 4px 20px rgba(1, 20, 124, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
        textButton: {
          hoverBackgroundColor: "#EEF0FF",
          hoverForegroundColor: "#1A1A2E",
        },
      },
      primaryButton: {
        backgroundColor: "#01147C", // Disney signature blue
        foregroundColor: "#FFFFFF", // White text on blue
        hoverBackgroundColor: Color.parse("#01147C").lighten(1.3).toHexString(),
        hoverForegroundColor: "#FFFFFF",
        shadow: "none",
        borderColor: "transparent",
      },
      secondaryButton: {
        backgroundColor: "#113CCF", // Lighter Disney blue
        foregroundColor: "#FFFFFF",
        hoverBackgroundColor: Color.parse("#113CCF").lighten(0.9).toHexString(),
        hoverForegroundColor: "#FFFFFF",
        shadow: "none",
        borderColor: "transparent",
      },
    },
    chartjs: {
      defaultColor: "#1A1A2E",
      defaultBackgroundColor: "rgba(1, 20, 124, 0.8)",
      defaultBorderColor: "transparent",
      colorPalette: [
        "#01147C", // Disney blue
        "#FF4500", // Orange red
        "#FFD700", // Magic gold
        "#00CED1", // Frozen cyan
        "#BA55D3", // Orchid purple
        "#32CD32", // Lime green
        "#FF6347", // Tomato orange
        "#87CEEB", // Sky blue
        "#FFFF00", // Bright yellow
        "#7B68EE", // Medium slate blue
        "#FF69B4", // Bright pink
        "#20B2AA", // Light sea green
      ],
      gridColor: "#E8E8F0",
      gridBorderColor: "#D0D0E0",
      fontFamily: "Nunito, 'Varela Round', system-ui, sans-serif",
      fontSize: 12,
      tooltipBackgroundColor: "#01147C",
      tooltipTextColor: "#FFFFFF",
      tooltipBorderColor: "#113CCF",
      legendTextColor: "#1A1A2E",
      axisTickColor: "#5C5C7A",
      lineTension: 0.4,
      lineBorderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      barBorderWidth: 0,
      barBorderRadius: 8,
      barBackgroundOpacity: 0.85,
      plugins: [
        createBarGradientPlugin({
          startLuminosity: 1.3,
          endLuminosity: 1.0,
          startOpacity: 0.7,
          endOpacity: 0.8,
          gradientEndPosition: 0.5,
        }),
        createLineFillGradientPlugin({
          topOpacity: 0.6,
          bottomOpacity: 0.5,
          topLuminosity: 1.2,
          bottomLuminosity: 1.0,
          gradientEndPosition: 0.5,
        }),
      ],
    },
    agGrid: {
      baseTheme: "material",
      params: {
        browserColorScheme: "light",
        fontFamily: "Nunito, 'Varela Round', system-ui, sans-serif",
        backgroundColor: "#FFFFFF",
        foregroundColor: "#1A1A2E",
        borderColor: "#E8E8F0",
        headerBackgroundColor: "#F0F4FF",
        headerTextColor: "#01147C",
        oddRowBackgroundColor: "#FAFBFF",
        accentColor: "#01147C",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 700,
        spacing: 4,
        borderRadius: 8,
        wrapperBorderRadius: 8,
        cellHorizontalPaddingScale: 0.9,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
      },
    },
    extraCss: disneyCss,
    extraHtml: disneyHtml,
    setup: disneySetup,
  },
];

// Combine regular themes with experimental themes for the full theme list
export const allThemes: Theme[] = BuildFlags.enable_web_dashboard_experimental_themes
  ? [...themes, ...experimentalBrandThemes, ...otherExperimentalThemes]
  : themes;

// Registry of custom theme specs, keyed by full `custom:{uuid}` id. Populated from the resolved
// spec embedded in the dashboard GET response (registerCustomThemeSpec) before the theme is read.
const customThemeSpecs = new Map<string, ThemeSpec>();
// Cache of expanded Themes, keyed by the composite `${themeId}|${mode}|${background}` so a palette
// or custom id resolves once per (mode, background) combination rather than once per lookup.
const customThemeCache = new Map<string, Theme>();

/** The fallback theme id: Ocean palette, used with light + gradient-on when a lookup misses. */
const FALLBACK_PALETTE_ID = "palette:ocean";

/** Prefix on the composite cache key that identifies which resolved themes belong to `themeId`. */
const compositeCacheKey = (themeId: string, mode: ThemeMode, background: ThemeBackground): string =>
  `${themeId}|${mode}|${background}`;

/**
 * Register a resolved custom ThemeSpec so `resolveTheme("custom:{uuid}", …)` can expand it. Call
 * this with the `resolved_theme` embedded in the dashboard GET response. Re-registering the same id
 * (e.g. after the theme was updated in place) replaces the spec and invalidates EVERY cached
 * (mode, background) combination for that id — a single-key delete would leave stale composites.
 */
export const registerCustomThemeSpec = (themeId: string, spec: ThemeSpec): void => {
  customThemeSpecs.set(themeId, spec);
  const prefix = `${themeId}|`;
  for (const key of customThemeCache.keys()) {
    if (key.startsWith(prefix)) {
      customThemeCache.delete(key);
    }
  }
};

/** Expand a built-in palette + toggles into a compact ThemeSpec for buildThemeFromSpec. */
export const specFromPalette = (
  palette: BuiltinPalette,
  mode: ThemeMode,
  background: ThemeBackground
): ThemeSpec => ({
  name: palette.name,
  mode,
  colors: palette.colors,
  background: { type: background === "on" ? "gradient" : "solid" },
  semantics: {
    positive: palette.semantics.text.positive,
    negative: palette.semantics.text.negative,
    neutral: palette.semantics.neutral,
    chart_positive: palette.semantics.chart.positive,
    chart_negative: palette.semantics.chart.negative,
  },
});

/**
 * Build the Theme for a themeId + toggles, or `null` if the id is unknown (unregistered custom, or
 * an unrecognized legacy slug). Legacy bare-slug themes ignore mode/background: they render exactly
 * as authored (the A′ baseline).
 */
const buildResolvedTheme = (
  themeId: string,
  mode: ThemeMode,
  background: ThemeBackground
): Theme | null => {
  if (isPaletteThemeId(themeId)) {
    const palette = BUILTIN_PALETTES.find(p => p.id === themeId);
    return palette ? buildThemeFromSpec(specFromPalette(palette, mode, background), themeId) : null;
  }
  if (isCustomThemeId(themeId)) {
    const spec = customThemeSpecs.get(themeId);
    if (!spec) {
      return null;
    }
    // Toggles override the custom spec's own mode/background type — a deliberate user decision.
    // Pinned light_color/dark_color are preserved: they're extracted brand page colors, not a toggle.
    const resolved: ThemeSpec = {
      ...spec,
      mode,
      background: { ...spec.background, type: background === "on" ? "gradient" : "solid" },
    };
    return buildThemeFromSpec(resolved, themeId);
  }
  return allThemes.find(t => t.themeId === themeId) ?? null;
};

/**
 * Resolve a themeId + selector toggles into a full Theme:
 *   - `palette:*`  → the built-in palette expanded for (mode, background)
 *   - `custom:*`   → the registered spec, with toggles overriding its own mode/background
 *   - legacy slug  → the authored theme, unchanged (mode/background ignored — A′ baseline)
 *   - anything else / missing → Ocean + light + gradient-on
 * Results are cached per `${themeId}|${mode}|${background}`.
 */
export const resolveTheme = (
  themeId: string | undefined | null,
  mode: ThemeMode,
  background: ThemeBackground
): Theme => {
  if (!themeId) {
    return resolveTheme(FALLBACK_PALETTE_ID, DEFAULT_THEME_MODE, DEFAULT_THEME_BACKGROUND);
  }
  const cacheKey = compositeCacheKey(themeId, mode, background);
  const cached = customThemeCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const built = buildResolvedTheme(themeId, mode, background);
  if (!built) {
    return resolveTheme(FALLBACK_PALETTE_ID, DEFAULT_THEME_MODE, DEFAULT_THEME_BACKGROUND);
  }
  customThemeCache.set(cacheKey, built);
  return built;
};

/**
 * Get a theme by its ID, using the default selector toggles (light + gradient-on). Legacy entry
 * point for callers that only have an id; delegates to `resolveTheme`. A `custom:`/`palette:` id
 * with no match (deleted/missing) falls back to Ocean.
 */
export const getThemeById = (themeId: string | undefined | null): Theme =>
  resolveTheme(themeId, DEFAULT_THEME_MODE, DEFAULT_THEME_BACKGROUND);

export interface ThemeOption {
  value: string;
  label: string;
  separator?: boolean; // If true, render a separator before this option
  isExperimental?: boolean; // If true, show a "Experimental" chip next to the option
}

/**
 * Get all available theme options for the theme selector.
 * Includes both regular themes and experimental themes, with a separator before
 * experimental themes.
 */
export const getThemeOptions = (): ThemeOption[] => {
  const options: ThemeOption[] = themes.map(t => ({
    value: t.themeId,
    label: t.themeName,
  }));

  if (BuildFlags.enable_web_dashboard_experimental_themes) {
    // Add experimental brand themes with a separator before the first one
    experimentalBrandThemes.forEach((t, index) => {
      options.push({
        value: t.themeId,
        label: t.themeName,
        separator: index === 0,
        isExperimental: true,
      });
    });

    // Add other experimental themes with a separator before the first one
    otherExperimentalThemes.forEach((t, index) => {
      options.push({
        value: t.themeId,
        label: t.themeName,
        separator: index === 0,
        isExperimental: true,
      });
    });
  }

  return options;
};

/**
 * Check if a theme is a dark theme based on AG Grid's browserColorScheme.
 */
export const isDarkTheme = (themeId: string | undefined | null): boolean => {
  const theme = getThemeById(themeId);
  return theme.agGrid.params.browserColorScheme === "dark";
};
